import type { ReactNode } from 'react';
import type { UseFormRegister } from 'react-hook-form';

import { FormField } from '@/shared/ui';

import type { FormFieldConfig, FormFieldHelperLink, FormSubmitValues } from './form-widget.types';
import { FieldInfo } from './FormPresentation';
import { getFieldContainerClass, getFieldDisplay } from './form-display';

const HELPER_LINK_TOKEN = '{link}';

function renderHelper(text: string | undefined, link: FormFieldHelperLink | undefined): ReactNode {
  if (!text) return undefined;
  if (!link) return text;

  const [before, after] = text.includes(HELPER_LINK_TOKEN)
    ? text.split(HELPER_LINK_TOKEN, 2)
    : [`${text} `, ''];

  return (
    <>
      {before}
      <a
        href={link.href}
        className="text-adipa-primary font-medium underline underline-offset-2 hover:no-underline"
      >
        {link.label}
      </a>
      {after}
    </>
  );
}

export type CommonInputProps = {
  id: string;
  hasError: boolean;
  'aria-describedby': string;
};

export function FieldFrame({ children, field }: { children: ReactNode; field: FormFieldConfig }) {
  return <div className={getFieldContainerClass(field)}>{children}</div>;
}

export function FieldShell({
  children,
  error,
  field,
  fieldDisplay,
  fieldId,
}: {
  children: ReactNode;
  error?: string;
  field: FormFieldConfig;
  fieldDisplay: ReturnType<typeof getFieldDisplay>;
  fieldId: string;
}) {
  return (
    <FieldFrame field={field}>
      <FormField
        label={fieldDisplay.label}
        htmlFor={fieldId}
        error={error}
        required={field.required}
        hint={renderHelper(fieldDisplay.helperText, fieldDisplay.helperLink)}
      >
        {children}
        <FieldInfo text={field.infoText} />
      </FormField>
    </FieldFrame>
  );
}

export function HiddenField({
  name,
  register,
  value,
}: {
  name: string;
  register: UseFormRegister<FormSubmitValues>;
  value: FormSubmitValues[string];
}) {
  return (
    <input
      type="hidden"
      value={typeof value === 'string' ? value : ''}
      {...register(name)}
      readOnly
    />
  );
}
